// The rules, rendered.
//
// A reading page rather than an overlay on the board: this is the one screen
// somebody sits with, and putting it over the game would make it something to
// dismiss rather than something to read. It is reachable from the menu and
// from a game, and it closes back to wherever it was opened from.
//
// **The document is a component, the page is a frame round it** (`RulesDocument`
// below). The pause menu shows the same document without leaving the game
// (PLAN-UI §28.3), and a second copy of the locale state, the dynamic import
// and the anchor scroll is two documents that drift. So nothing in the document
// sets a background, a measure or a scroll — all three belong to the frame, and
// the plate is the other frame.

import { MDXProvider } from "@mdx-js/react";
import type { MDXComponents } from "mdx/types";
import { type ComponentType, useEffect, useRef, useState } from "react";

import { type Locale, LOCALES, pickLocale } from "src/client/ai/content/rules";

import { Footer } from "./Footer";
import { RULES_COMPONENTS } from "./rulesComponents";
import {
  Btn,
  Link,
  Notice,
  Page,
  PageColumn,
  PageTitle,
  Select,
  styled
} from "./kit";

/** A section heading. Cream, because the brand is what a control is. */
const Heading = styled("h2")(({ theme }) => ({
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 20,
  lineHeight: 1.2,
  color: theme.kit.color.cream,
  margin: `${theme.kit.space.xl}px 0 ${theme.kit.space.sm}px`
}));

// **Body prose is `ink`, not `inkDim`.** A pause screen's `Copy` is a caption
// beside a control and can afford to recede; this is the one screen somebody
// sits and reads, and legibility is the measured risk (PLAN §7.1).
const Para = styled("p")(({ theme }) => ({
  margin: `${theme.kit.space.md}px 0`,
  color: theme.kit.color.ink,
  fontSize: 14,
  lineHeight: 1.7
}));

const List = styled("ul")(({ theme }) => ({
  margin: `${theme.kit.space.md}px 0`,
  paddingLeft: theme.kit.space.xl,
  color: theme.kit.color.ink,
  fontSize: 14
}));

const Item = styled("li")(({ theme }) => ({
  marginBottom: theme.kit.space.xs,
  lineHeight: 1.7
}));

const Strong = styled("strong")(({ theme }) => ({
  color: theme.kit.color.cream,
  fontWeight: 700
}));

const Em = styled("em")(({ theme }) => ({ color: theme.kit.color.cream }));

/**
 * How the document's plain markdown renders.
 *
 * Set here rather than in the .mdx so a translator never has to touch styling,
 * and so every locale looks the same without anybody remembering to make it.
 */
const MARKDOWN: MDXComponents = {
  // The document opens with its own title, which *is* the page's — so the h1
  // is `PageTitle` rather than a second heading under one.
  h1: (props) => <PageTitle {...props} />,
  h2: (props) => <Heading {...props} />,
  p: (props) => <Para {...props} />,
  ul: (props) => <List {...props} />,
  li: (props) => <Item {...props} />,
  strong: (props) => <Strong {...props} />,
  em: (props) => <Em {...props} />,
  a: (props) => <Link {...props} />
};

const COMPONENTS: MDXComponents = { ...MARKDOWN, ...RULES_COMPONENTS };

/** The locale picker sits at the right of its own row, above the document. */
const LocaleRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: theme.kit.space.sm
}));

/**
 * Boxed to a control's width rather than left to fill: `Select` is a block that
 * takes its container, and a language picker as wide as the prose reads as a
 * banner across the top of the document.
 */
const LocaleBox = styled("div")({ width: 180 });

/** The back bar. Sticky, because a long document is a long way from its exit. */
const BackBar = styled("div")(({ theme }) => ({
  position: "sticky",
  top: 0,
  padding: `${theme.kit.space.md}px ${theme.kit.space.xl}px`,
  background: `color-mix(in srgb, ${theme.kit.color.ground} 90%, transparent)`,
  backdropFilter: "blur(8px)",
  borderBottom: `2px solid ${theme.kit.color.line}`
}));

/**
 * The rules document itself — locale picker, the loaded MDX, and the scroll to
 * an anchor. Everything the reader reads; nothing about where it is framed.
 *
 * Used by `RulesPage` (its own screen, with a back bar and the footer) and by
 * the pause menu's Rules screen (framed by the plate, no footer).
 */
export function RulesDocument({
  anchor
}: {
  /**
   * A heading to open at, from a hint's `Concept.anchor` (§16.4 tier three).
   *
   * Matched by **slugifying the rendered headings** rather than by `id`,
   * because the MDX pipeline does not mint ids and adding a rehype plugin to
   * make one work would put a build step behind a scroll position. A miss is
   * silent and lands at the top, which is the page the reader asked for
   * anyway.
   */
  anchor?: string;
}) {
  const [locale, setLocale] = useState<Locale>(() =>
    pickLocale(typeof navigator === "undefined" ? [] : navigator.languages ?? [])
  );
  const [Doc, setDoc] = useState<ComponentType<{ components?: MDXComponents }> | null>(
    null
  );
  const [failed, setFailed] = useState(false);
  // **The search is scoped to this document, not to `document`.** The pause
  // menu can have the rules mounted beside other screens' headings, and a
  // global sweep would scroll to whichever matched first.
  const root = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let live = true;
    setDoc(null);
    setFailed(false);
    // A chunk load can fail — an offline reader, or a deploy that replaced the
    // file mid-session. Saying so beats an empty page that looks like the
    // rules are simply missing.
    locale
      .load()
      .then((module) => live && setDoc(() => module.default))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [locale]);

  // After the document lands, not with it: the headings do not exist until the
  // chunk has rendered, and a scroll aimed at nothing is a scroll to the top.
  useEffect(() => {
    if (!Doc || !anchor) return;
    const slug = (text: string) =>
      text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    const frame = requestAnimationFrame(() => {
      const container = root.current;
      if (!container) return;
      for (const heading of container.querySelectorAll("h1, h2, h3")) {
        if (slug(heading.textContent ?? "") !== anchor) continue;
        heading.scrollIntoView({ block: "start" });
        return;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [Doc, anchor]);

  return (
    <div ref={root}>
      <LocaleRow>
        {/*
          Shown even with one language in it. The picker is the promise that
          this page is translatable, and hiding it until a second locale exists
          means nobody discovers it the day one arrives.
        */}
        <LocaleBox>
          <Select
            value={locale.code}
            data-testid="rules-locale"
            aria-label="Language"
            onChange={(event) => {
              const next = LOCALES.find((entry) => entry.code === event.target.value);
              if (next) setLocale(next);
            }}
          >
            {LOCALES.map((entry) => (
              <option key={entry.code} value={entry.code}>
                {entry.name}
              </option>
            ))}
          </Select>
        </LocaleBox>
      </LocaleRow>

      {failed && (
        <Notice tone="warn">
          The rules could not be loaded. Reloading the page usually fixes it.
        </Notice>
      )}
      {Doc && (
        <MDXProvider components={COMPONENTS}>
          <Doc components={COMPONENTS} />
        </MDXProvider>
      )}
    </div>
  );
}

export function RulesPage({
  onClose,
  anchor
}: {
  onClose(): void;
  /** A heading to open at — see `RulesDocument`. */
  anchor?: string;
}) {
  return (
    <Page data-testid="rules-page">
      <BackBar>
        <Btn variant="ghost" small data-testid="rules-back" onClick={onClose}>
          ← Back
        </Btn>
      </BackBar>

      <PageColumn width={880}>
        <RulesDocument anchor={anchor} />
        <Footer />
      </PageColumn>
    </Page>
  );
}
